import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { router } from "..";
import type { Route, RouteContext } from "../router";
import { state } from "../state";
import { centeredBox } from "../van/centered-box";
import { a, button, div, hr, input, li, nav, pre, ul } from "../van/van-tags";
import { spPlaceholder } from "./sp";

export class AdminSpPage implements Route {
	async #addTrial(): Promise<void> {
		const req = this.#ctx.api.put(
			`/smartplugs/${this.#reactive.smartplug.Imei}/trial`,
		);
		req.setJsonBody({
			Days: +this.#trialDays,
		});
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.smartplug = await res.json();
			this.#trialDays = "";
		}
	}

	async #cancelSubscription(): Promise<void> {
		const req = this.#ctx.api.delete(
			`/smartplugs/${this.#reactive.smartplug.Imei}/subscription`,
		);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.smartplug = await res.json();
		}
	}

	async #impersonate(): Promise<void> {
		const req = this.#ctx.api.get(
			`/admin/impersonate?key=smartplug&value=${this.#reactive.smartplug.Imei}`,
		);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			state.startImpersonating(await res.json());
			this.#ctx.router.jump(
				`/devices?id=${this.#reactive.smartplug.Imei}`,
			);
		}
	}

	async refresh(match: Match): Promise<void> {
		if (!match.data?.imei) throw "missing path param: imei";
		const imei = match.data.imei;

		const req = this.#ctx.api.get(`/smartplugs/${imei}`);
		req.setSignedInUserAuth();

		const res = await req.do();
		if (res.ok) {
			this.#reactive.smartplug = await res.json();
		}
	}

	#ctx: RouteContext;
	#reactive = reactive({
		smartplug: spPlaceholder,
	});
	#trialDays = "";

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{
				size: "wide",
			},
			nav(
				{
					class: "breadcrumb",
				},
				ul(
					li(
						a(
							{
								onclick: () => router.jump("/admin"),
							},
							"Admin",
						),
					),
					li(a("SmartPlug")),
				),
			),
			div(
				{
					class: "buttons",
				},
				button(
					{
						class: "button is-primary",
						onclick: () => this.#impersonate(),
					},
					"Impersonate owner",
				),
				button(
					{
						class: "button is-primary",
						onclick: () => this.#cancelSubscription(),
					},
					"Cancel subscription",
				),
			),
			div(
				{
					class: "field has-addons",
				},
				div(
					{
						class: "control",
					},
					input({
						class: "input",
						oninput: (e: Event) => {
							if (!(e.target instanceof HTMLInputElement)) return;
							this.#trialDays = e.target.value.replace(/\D/g, "");
							e.target.value = this.#trialDays;
						},
						placeholder: "Days",
					}),
				),
				div(
					{
						class: "control",
					},
					button(
						{
							class: "button is-primary",
							onclick: () => this.#addTrial(),
						},
						"Add trial",
					),
				),
			),
			hr(),
			pre({ class: "is-tiny-mobile" }, () =>
				JSON.stringify(this.#reactive.smartplug, null, 2),
			),
		);
	}
}
